import React, { useState } from 'react';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { supabase, syncSessionCookie } from '../lib/supabase';

const TERMS_VERSION = '1.2';
const TERMS_EFFECTIVE = '13 April 2026';

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!acknowledged) return;
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const { error: authError, data } = await supabase.auth.signInWithPassword({ email, password });
        if (authError) {
          setError(authError.message);
          return;
        }
        if (data.session) {
          await syncSessionCookie(data.session);
        }
      } else {
        const { error: authError, data } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { first_name: firstName, last_name: lastName },
          },
        });
        if (authError) {
          setError(authError.message);
          return;
        }
        if (data.session) {
          await syncSessionCookie(data.session);
        } else {
          setError('Check your email to confirm your account before signing in.');
          return;
        }
      }
      window.location.href = '/account';
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    if (!acknowledged) return;
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
  }

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Left Panel - Image/Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-container relative overflow-hidden flex-col justify-between p-12 text-on-primary">
        <div className="relative z-10">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <Logo className="h-12 text-on-primary" />
          </Link>
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="text-4xl font-headline font-medium mb-6">
            {isLogin ? "Welcome back." : "Start modelling scenarios."}
          </h2>
          <p className="text-on-primary/80 font-body text-lg mb-8">
            {isLogin ? "Access your scenario models, saved sites, and indicative feasibility runs." : "Early-stage feasibility scenario modelling for property professionals."}
          </p>
          {/* Inline disclaimer on left panel */}
          <div className="bg-on-primary/10 border border-on-primary/20 rounded-lg p-5">
            <p className="text-xs text-on-primary/80 font-body leading-relaxed">
              <strong className="block mb-2">General information only</strong>
              This tool provides scenario modelling and general information only. It is not financial, legal, tax, valuation, planning, or development advice. All outputs are indicative and must be independently verified before acting.
            </p>
          </div>
        </div>
        <div className="absolute inset-0 opacity-20 mix-blend-multiply">
          <img src="https://lh3.googleusercontent.com/aida-public/AB6AXuD5KTfl3tmvIz2Y1Vgfu-vshVRVYSxj0ZPOhAFqhjVLiAt2x-pd4St4WyEzoH_IMYofqna1MTk9gVUxbrJVB-WsPSNelJeydkeCCsIeEFgHqEGAo5iiehi6f5ig9sjI8lBPzRR9E04007PbS00X5aDxkqYsKRFKd0Njumle1ySl8EEjtwAubSbI224DliNMCcS96x05Fvrr3Gsbhpwwyhy9duKn0qOGFPdE0FckoUieJVprIHW4SH3DXXCSqvtrsHBUZsX1l9egtMAa" alt="Architecture" className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
              <Logo className="h-10 text-primary" />
            </Link>
          </div>

          <h1 className="text-3xl font-headline font-bold text-primary mb-2">
            {isLogin ? "Sign In" : "Create Account"}
          </h1>
          <p className="text-on-surface-variant font-body mb-8">
            {isLogin ? "Choose your preferred method to access your workspace." : "Choose your preferred method to start your free trial."}
          </p>

          {/* Pre-use Acknowledgement Gate */}
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-5">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
              <p className="text-sm font-bold text-amber-900">Before you continue — please read and confirm</p>
            </div>
            <p className="text-xs text-amber-800 leading-relaxed mb-4">
              I understand this calculator provides <strong>general information and scenario modelling only</strong>. It is not legal, financial, tax, valuation, planning, engineering, building, or development advice.
              Results are indicative only, depend on assumptions and user inputs, and <strong>must not be relied on as the sole or primary basis for any acquisition, pricing, lending, investment, or development decision</strong>.
              I will obtain independent professional advice and verify all inputs, assumptions, constraints, and outputs before acting.
            </p>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={e => setAcknowledged(e.target.checked)}
                className="mt-0.5 shrink-0 accent-primary w-4 h-4"
              />
              <span className="text-xs text-amber-900 font-body leading-relaxed group-hover:text-amber-800">
                I confirm I have read and understood the above. I acknowledge this is general information only and not professional advice.
              </span>
            </label>
            <p className="text-[10px] text-amber-700 mt-3 font-body">
              Terms version {TERMS_VERSION} — effective {TERMS_EFFECTIVE}. By continuing you agree to the{' '}
              <Link to="/legal/terms-of-use" className="underline hover:text-amber-900">Terms of Use</Link>.
            </p>
          </div>

          <div className="mb-6">
            <button
              type="button"
              disabled={!acknowledged}
              onClick={handleGoogleSignIn}
              className="w-full bg-white border border-outline-variant/30 text-primary py-3.5 font-bold uppercase tracking-widest rounded-sm hover:bg-surface-container-low transition-colors flex justify-center items-center gap-3 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative mt-8 mb-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-outline-variant/30"></div>
              </div>
              <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-widest">
                <span className="bg-surface px-4 text-on-surface-variant">Or continue with email</span>
              </div>
            </div>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-primary">First Name</label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    className="w-full bg-transparent border border-outline-variant/30 rounded-sm px-4 py-3 focus:outline-none focus:border-primary transition-colors font-body"
                    placeholder="Marcus"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-primary">Last Name</label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    className="w-full bg-transparent border border-outline-variant/30 rounded-sm px-4 py-3 focus:outline-none focus:border-primary transition-colors font-body"
                    placeholder="Chen"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-primary">Email Address</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-transparent border border-outline-variant/30 rounded-sm px-4 py-3 focus:outline-none focus:border-primary transition-colors font-body"
                placeholder="marcus@example.com"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold uppercase tracking-widest text-primary">Password</label>
                {isLogin && <a href="#" className="text-xs font-bold text-primary/60 hover:text-primary transition-colors">Forgot?</a>}
              </div>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-transparent border border-outline-variant/30 rounded-sm px-4 py-3 focus:outline-none focus:border-primary transition-colors font-body"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 font-body">{error}</p>
            )}

            {!acknowledged && (
              <p className="text-xs text-amber-700 font-body">Please confirm the acknowledgement above to continue.</p>
            )}

            <button
              type="submit"
              disabled={loading || !acknowledged}
              className="w-full bg-primary text-on-primary py-4 font-bold uppercase tracking-widest rounded-sm hover:bg-primary-container transition-colors mt-4 flex justify-center items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait…' : (isLogin ? 'Sign In' : 'Create Account')}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="mt-8 text-center space-y-6">
            <p className="text-on-surface-variant font-body text-sm">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-primary font-bold hover:underline">
                {isLogin ? "Start free trial" : "Sign in"}
              </button>
            </p>

            <div className="pt-6 border-t border-outline-variant/20">
              <p className="text-[11px] leading-relaxed text-on-surface-variant/80 font-body text-left">
                By continuing, you agree to Site Yield's{' '}
                <Link to="/legal/terms-of-use" className="underline hover:text-primary transition-colors">Terms of Use</Link>,{' '}
                <Link to="/legal/privacy-policy" className="underline hover:text-primary transition-colors">Privacy Policy</Link>, and{' '}
                <Link to="/legal/cookie-notice" className="underline hover:text-primary transition-colors">Cookie Notice</Link>.
                <br /><br />
                <strong>Output Disclaimer:</strong> Site Yield provides general information and scenario modelling only. This platform does not provide financial, legal, tax, valuation, planning, engineering, or architectural advice. All outputs are indicative only and must be independently verified by qualified professionals before making any acquisition, lending, investment, or development decision.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
